import * as React from 'react';
import type {
  ProgrammaticDashboardProps,
  ProgrammaticFilters,
  ProgrammaticMetrics,
  ProgrammaticRegionData,
  ProgrammaticPagination,
  ProgrammaticApiResponse,
} from '../../types/programmatic';
import ProgrammaticMetricsCards from './ProgrammaticMetricsCards';
import ProgrammaticFilterPanel from './ProgrammaticFilterPanel';
import ProgrammaticMapView from './ProgrammaticMapView';
import ProgrammaticDataTable from './ProgrammaticDataTable';

const defaultFilters = (): ProgrammaticFilters => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 90);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
    geoLevel: 'states',
    mapMode: 'native_strength',
    selectedState: null,
    selectedCity: null,
  };
};

const ProgrammaticAnalyticsDashboard: React.FC<ProgrammaticDashboardProps> = ({
  apiEndpoint,
  className = '',
}) => {
  const [filters, setFilters] = React.useState<ProgrammaticFilters>(defaultFilters);
  const [metrics, setMetrics] = React.useState<ProgrammaticMetrics | null>(null);
  const [regionData, setRegionData] = React.useState<ProgrammaticRegionData[]>([]);
  const [pagination, setPagination] = React.useState<ProgrammaticPagination | undefined>();
  const [statesList, setStatesList] = React.useState<string[]>([]);
  const [metricsLoading, setMetricsLoading] = React.useState(true);
  const [dataLoading, setDataLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const abortRef = React.useRef<AbortController | null>(null);

  const getCsrfToken = (): string => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') || '' : '';
  };

  const fetchJson = React.useCallback(async (url: string, signal?: AbortSignal) => {
    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': getCsrfToken(),
      },
      signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }, []);

  // Fetch metrics summary
  const fetchMetrics = React.useCallback(async (signal?: AbortSignal) => {
    setMetricsLoading(true);
    try {
      const data = await fetchJson(
        `${apiEndpoint}/metrics_summary?start_date=${filters.startDate}&end_date=${filters.endDate}`,
        signal
      );
      setMetrics(data);
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('Failed to fetch metrics:', err);
      }
    } finally {
      setMetricsLoading(false);
    }
  }, [apiEndpoint, filters.startDate, filters.endDate, fetchJson]);

  // Fetch region data based on geo level
  const fetchRegionData = React.useCallback(async (signal?: AbortSignal) => {
    setDataLoading(true);
    setError(null);
    try {
      let url: string;
      const dateParams = `start_date=${filters.startDate}&end_date=${filters.endDate}`;

      switch (filters.geoLevel) {
        case 'cities':
          url = `${apiEndpoint}/supply_by_city?${dateParams}&page=${page}&per_page=50`;
          if (filters.selectedState) url += `&state=${filters.selectedState}`;
          break;
        case 'zipcodes':
          url = `${apiEndpoint}/supply_by_zipcode?${dateParams}&page=${page}&per_page=50`;
          if (filters.selectedState) url += `&state=${filters.selectedState}`;
          if (filters.selectedCity) url += `&city=${filters.selectedCity}`;
          break;
        default:
          url = `${apiEndpoint}/supply_by_state?${dateParams}`;
      }

      const result: ProgrammaticApiResponse = await fetchJson(url, signal);
      setRegionData(result.data || []);
      setPagination(result.pagination);

      // Build states list from state-level data
      if (filters.geoLevel === 'states' && result.data) {
        setStatesList(result.data.map((r: ProgrammaticRegionData) => r.state || '').filter(Boolean));
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setError('Failed to load data. Please try again.');
        console.error('Failed to fetch region data:', err);
      }
    } finally {
      setDataLoading(false);
    }
  }, [apiEndpoint, filters, page, fetchJson]);

  // Initial load + refetch on filter/page changes
  React.useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    fetchMetrics(controller.signal);
    fetchRegionData(controller.signal);

    return () => controller.abort();
  }, [fetchMetrics, fetchRegionData]);

  const handleFilterChange = (partial: Partial<ProgrammaticFilters>) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, ...partial }));
  };

  const handleStateClick = (stateCode: string) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      geoLevel: 'cities',
      selectedState: stateCode,
    }));
  };

  const handleRowClick = (region: ProgrammaticRegionData) => {
    if (filters.geoLevel === 'states' && region.state) {
      handleStateClick(region.state);
    } else if (filters.geoLevel === 'cities' && region.city) {
      setPage(1);
      setFilters((prev) => ({
        ...prev,
        geoLevel: 'zipcodes',
        selectedState: region.state || prev.selectedState,
        selectedCity: region.city || null,
      }));
    }
  };

  const handleClearCache = async () => {
    try {
      await fetch(`${apiEndpoint}/clear_cache`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json',
        },
      });
      // Refetch after clearing
      fetchMetrics();
      fetchRegionData();
    } catch (err) {
      console.error('Failed to clear cache:', err);
    }
  };

  return (
    <div className={className}>
      {/* Metrics Cards */}
      <ProgrammaticMetricsCards metrics={metrics} isLoading={metricsLoading} />

      {/* Filter Panel */}
      <ProgrammaticFilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        states={statesList}
        isLoading={dataLoading}
      />

      {/* Error State */}
      {error && (
        <div className="alert alert-danger d-flex align-items-center mb-4">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => fetchRegionData()}>
            <i className="fas fa-redo me-1"></i>Retry
          </button>
        </div>
      )}

      {/* Map (only for state-level view) */}
      {filters.geoLevel === 'states' && (
        <ProgrammaticMapView
          data={regionData}
          mapMode={filters.mapMode}
          isLoading={dataLoading}
          onStateClick={handleStateClick}
        />
      )}

      {/* Data Table */}
      <ProgrammaticDataTable
        data={regionData}
        geoLevel={filters.geoLevel}
        pagination={pagination}
        onPageChange={setPage}
        onRowClick={filters.geoLevel !== 'zipcodes' ? handleRowClick : undefined}
        isLoading={dataLoading}
      />

      {/* Footer */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Data cached for 1 hour. Last generated: {metrics?.generated_at ? new Date(metrics.generated_at).toLocaleString() : 'N/A'}
        </small>
        <button className="btn btn-sm btn-outline-secondary" onClick={handleClearCache}>
          <i className="fas fa-sync-alt me-1"></i>Refresh Cache
        </button>
      </div>
    </div>
  );
};

ProgrammaticAnalyticsDashboard.displayName = 'ProgrammaticAnalyticsDashboard';
export default ProgrammaticAnalyticsDashboard;
